export type Dependent = {
  dependentId: string;
  fullName: string;
  dob: string | null; // ISO string or null if unavailable
  relationship?: string;
  idNumber?: string;
};

export type PolicyLookupSuccess = {
  policyId: string;
  policyholder: {
    fullName: string;
    idNumber: string;
    dob: string | null; // ISO string or null
  };
  dependents: Dependent[];
  status: "active" | "inactive";
  policyholderRiskId?: string;
  // Resolved from API response, using first member (index 0)
  resolvedPolicyNumber?: string;
  resolvedIdNumber?: string;
  resolvedAddressLine1?: string;
  resolvedAddressLine2?: string;
  resolvedCity?: string;
  resolvedZipCode?: string;
  resolvedMobileNo?: string;
  resolvedEmail?: string;
};

export type PolicyLookupErrorCode = "NOT_FOUND" | "INACTIVE" | "MISMATCH_ID";

export class PolicyLookupError extends Error {
  code: PolicyLookupErrorCode;
  constructor(code: PolicyLookupErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

type SiragoPolicyMember = {
  iPolicyID: number;
  PolicyNumber: string;
  PolicyStatus: string; // "Active" | ...
  RiskStatus: string; // "Active" | ...
  intRiskID: number;
  strDescription: string; // contains role like "Principal", "Spouse", etc
  FirstName: string;
  LastName: string;
  DateOfBirth: string | null;
  IdNumber: string;
  // Address fields (may be missing on some responses)
  strAddressLine1?: string;
  strAddressLine2?: string;
  strAddressLine3?: string;
  strSuburb?: string;
  strRatingArea?: string;
  strPostalCode?: string;
  // Contact fields (may be present)
  strMobileNo?: string;
  strEmailAdd?: string;
};

type SiragoPolicyDetailsResponse = {
  data: SiragoPolicyMember[];
};

function toTitleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function mapToSuccess(
  members: SiragoPolicyMember[],
  providedIdNumber: string
): PolicyLookupSuccess {
  if (!members || members.length === 0) {
    throw new PolicyLookupError(
      "NOT_FOUND",
      "We couldn't find a policy with the provided details."
    );
  }

  const statusStr = (members[0].PolicyStatus || "").toLowerCase();
  const isActive = statusStr === "active";
  if (!isActive) {
    throw new PolicyLookupError("INACTIVE", "This policy is inactive.");
  }

  const firstMember = members[0];

  const principal =
    members.find((m) => /principal/i.test(m.strDescription)) || members[0];

  const dependents: Dependent[] = members
    .filter(
      (m) => m !== principal && (m.RiskStatus || "").toLowerCase() === "active"
    )
    .map((m) => ({
      dependentId: String(m.intRiskID),
      fullName: `${m.FirstName || ""} ${m.LastName || ""}`.trim(),
      dob: m.DateOfBirth,
      idNumber: m.IdNumber,
      relationship: (m.strDescription.match(/\)\s*(\w+)\s*$/) || [])[1],
    }));

  return {
    policyId: String(principal.iPolicyID),
    policyholder: {
      fullName: `${principal.FirstName || ""} ${
        principal.LastName || ""
      }`.trim(),
      idNumber: principal.IdNumber,
      dob: principal.DateOfBirth,
    },
    dependents,
    status: isActive ? "active" : (toTitleCase(statusStr) as "inactive"),
    policyholderRiskId: String(principal.intRiskID),
    resolvedPolicyNumber: firstMember?.PolicyNumber,
    resolvedIdNumber: firstMember?.IdNumber,
    resolvedAddressLine1: firstMember?.strAddressLine1,
    resolvedAddressLine2: firstMember?.strAddressLine2,
    resolvedCity: firstMember?.strSuburb,
    resolvedZipCode: firstMember?.strPostalCode,
    resolvedMobileNo: firstMember?.strMobileNo,
    resolvedEmail: firstMember?.strEmailAdd,
  };
}

export async function policyLookup(
  idNumber: string,
  policyNumber: string
): Promise<PolicyLookupSuccess> {
  const trimmedIdNumber = (idNumber || "").trim();
  const trimmedPolicyNumber = (policyNumber || "").trim();

  if (!trimmedIdNumber) {
    throw new PolicyLookupError(
      "NOT_FOUND",
      "ID number is required for policy lookup."
    );
  }

  if (!trimmedPolicyNumber) {
    throw new PolicyLookupError(
      "NOT_FOUND",
      "Policy number is required for policy lookup."
    );
  }

  const body: { policyNr: string; idNumber: string } = {
    policyNr: trimmedPolicyNumber,
    idNumber: trimmedIdNumber,
  };

  const response = await fetch(
    "https://siragoapiprod.azure-api.net/siragoGap?cmd=getSiragoDependants",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new PolicyLookupError(
      "NOT_FOUND",
      "Unable to verify policy at this time."
    );
  }
  const json = (await response.json()) as SiragoPolicyDetailsResponse;
  const members = json?.data || [];
  return mapToSuccess(members, trimmedIdNumber);
}
